import { ReactElement } from "react";
import HeroSection from "@components/severalbrands/HeroSection";
import MetaTags from "@components/severalbrands/metaTags";
// Main general category shared components
import GeneralCategorySecondSection from "../b2b/secondSection";
import GeneralCategoryThirdSection from "../b2b/thirdSection";
import GeneralCategoryForthSection from "../b2b/forthSection";
// images and icons
import { useDomainContext } from "@hooks/useDomainContext";
// category data
const imgTextComponentContent = {
    img: "/severalbrands/assets/images/clinicalTrial.webp",
    mainTitle: "Optimizing Clinical Trial Recruitment with Targeted Marketing",
    paragraphs: [
        "Clinical trials are vital for advancing medical research, yet recruitment remains a significant hurdle, with 80% of trials facing delays due to participant enrollment challenges. As a lead generation company specializing in clinical trial recruitment, we understand the importance of connecting you with the right participants. Our compassionate approach ensures that potential participants are well-informed and supported throughout the process, fostering trust and engagement.",
        "We leverage advanced data analytics and targeted outreach strategies to reach diverse patient populations effectively. Our expertise in recruitment allows you to focus on your trial objectives while we handle the complexities of participant engagement. Let us help you overcome recruitment challenges and enhance your clinical trials. Contact us now to discover how our tailored solutions can drive your research success.",
    ],
    showAnimation: true,
    showButton: true,
    buttonTitle: "Schedule a Call",
};
const howWeWorkContent = {
    title: "Our Approach",
    list: [
        {
            title: "Collaborative Strategy Development",
            description:
                "We team up with your business to formulate a pricing strategy that aligns with your performance goals and fulfills your specific needs.",
        },
        {
            title: "Custom Campaign Creation",
            description:
                "By conducting in-depth research, we craft targeted campaigns that reach genuine customers throughout the United States looking for clinical trials to participate in. Thus, we optimize our approach for maximum conversion.",
        },
        {
            title: "Advanced Lead Verification",
            description:
                "We use advanced verification to ensure high-quality leads and reduce fraud and duplicates. Verified leads are sent to your intake center or ours, securing signed acquisitions for you.",
        },
        {
            title: "Performance Feedback Loop",
            description:
                "We actively seek feedback on lead effectiveness through regular performance reports, allowing us to refine our strategies and continuously enhance lead quality to connect you with your ideal clientele.",
        },
        {
            title: "KPI-Driven Success",
            description:
                "In collaboration with our partners, we establish clear Key Performance Indicators (KPIs) to measure cost-effectiveness and adjust compensation based on the achievement of these benchmarks.",
        },
        {
            title: "Ongoing Optimization",
            description:
                "We cyclically refine steps 2 through 4, aiming for a seamless process that consistently meets our shared KPIs and drives mutual satisfaction and success.",
        },
    ],
};
const partnerWitUSData = {
    title: "Why Partner With Us?",
    paragraphs: [
        {
            text: "Partnering with us means accessing a team of experts dedicated to your success. Our data-driven approach ensures that every marketing effort resonates with your target audience, driving measurable results and sustainable growth. We help you connect with the right clinical trial participants quickly and efficiently, accelerating your trials and moving your research forward with confidence.",
            className: "default",
        },
        {
            text: "Let us help you unlock your full potential and accelerate success in the medical field. Together, we can transform your marketing strategy into a powerful tool, ensuring that your clinical trials quickly reach the right audiences, so life-saving medicines get to those who need them faster.",
            className: "default",
        },
    ],
    buttonText: "Partner With Us",
};
const unlockBusinessContent = [
    {
        img: "/severalbrands/assets/images/unlockBusinessSuccessB2b2.webp",
        mainTitle:
            "Enhance Participant Recruitment with Diverse Marketing Approaches",
        secondTitle: "SEO & SEM",
        paragraphs: [
            "SEO provides sustainable growth at no extra cost but might not scale quickly. To accelerate our client's growth, we integrate SEM tactics. This strategy includes a specialized team for SEO content development and a robust team of over 20 SEM professionals managing paid campaigns across Google, Bing, and Yahoo. This dual approach ensures our digital marketing efforts are both effective and comprehensive.",
        ],

        mainSectionClassName: `3xl:gap-[200px] lg:gap-[130px] gap-[40px] `,
        mainTitleClassName: "text-[#351C4F]",
        secondTitleClassName: "mt-[20px] text-[#37367D]",
        imgClassName:
            "w-[250px]  h-[250px] md:w-[420px] md:min-w-[420px] md:h-[420px] relative 3xl:w-[620px] 3xl:min-w-[620px] 3xl:h-[620px] lg:mt-10 lg:ml-16 3xl:ml-28",
    },

    {
        img: "/severalbrands/assets/images/displayAdvertisingB2b.png",
        mainTitle: "Innovative Display Advertising",
        paragraphs: [
            "Our display advertising strategy employs a variety of platforms, incorporating the Google Display Network, native advertising with Taboola and Outbrain, PPV campaigns, and other external networks. With more than ten years in the field, we have honed our techniques to optimize conversion rates and deliver superior campaign performance consistently.",
        ],
        mainSectionClassName: `lg:gap-[160px] gap-[40px] lg:ml-16 3xl:ml-28 `,

        mainTitleClassName: "text-[#37367D] 3xl:mt-[60px]",
        imgToLeft: true,
        imgClassName:
            "w-[250px]  h-[200px] md:w-[458px] md:min-w-[458px] md:h-[312px] relative 3xl:w-[630px] 3xl:min-w-[630px] 3xl:h-[554px]",
    },

    {
        img: "/severalbrands/assets/images/localizationB2b2.webp",
        mainTitle: "Strategic Localization",
        paragraphs: [
            "Localization is crucial for effectively reaching multilingual audiences in clinical trials. Trials targeting both English and Spanish speakers can significantly enhance recruitment and engagement by adopting this strategy.",
        ],
        mainSectionClassName: `3xl:gap-[200px] lg:gap-[180px]  gap-[40px]  `,
        secondSectionClassName: "",
        mainTitleClassName: "text-[#37367D] lg:mt-10",

        imgClassName:
            "w-[250px]  h-[250px] md:w-[392px] md:min-w-[392px] md:h-[392px] 3xl:w-[592px] 3xl:min-w-[592px] 3xl:h-[592px]  relative lg:ml-16 3xl:ml-28",
    },
];
export default function ClinicalTrialsContainer(): ReactElement {
    const { category } = useDomainContext();
    const description = category?.metatags?.find(
        (meta) => meta.key === "description",
    )?.value;

    return (
        <>
            <MetaTags
                title={
                    category?.title ??
                    "Accelerate Clinical Trial Recruitment with Digital Marketing | SeveralBrands.com"
                }
                description={
                    description ??
                    "Boost your clinical trial recruitment with Several Brands' targeted digital marketing solutions. Efficient, cost-effective participant engagement starts here."
                }
            />
            <HeroSection />

            <GeneralCategorySecondSection
                mainData={{
                    imgTextComponentContent: imgTextComponentContent,
                    hideStrategicLeadGenerationContent: true,
                }}
            />

            <GeneralCategoryThirdSection
                mainData={{
                    howWeWorkContent: howWeWorkContent,
                    hideHowWeWorkContent: true,
                }}
            />
            <GeneralCategoryForthSection
                mainData={{
                    partnerWithUSData: partnerWitUSData,
                    unlockBusinessContent: unlockBusinessContent,
                }}
            />
        </>
    );
}
